// @ts-strict-ignore
import type { ReactNode } from "react";

import { Column, Columns } from "@aviary";
import { useStoreConfigQuery } from "@shared/data/queries/StoreConfigQuery/StoreConfig.query.generated";
import type { StoreConfigType } from "@shared/data/queries/StoreConfigQuery/StoreConfig.types";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { Page } from "@unauthenticated/signupSignin/components/Page/Page";
import { PatientInfoBlock } from "@unauthenticated/signupSignin/components/PatientInfoBlock/PatientInfoBlock";
import { StorefrontWelcomeLogo } from "@unauthenticated/signupSignin/components/StorefrontWelcomeLogo/StorefrontWelcomeLogo";
import { StoreLandingFooter } from "@unauthenticated/signupSignin/components/StoreLandingFooter/StoreLandingFooter";
import { StoreLandingPageNavBar } from "@unauthenticated/signupSignin/components/StoreLandingPageNavBar/StoreLandingPageNavBar";
import { StoreSignupInCarousel } from "@unauthenticated/signupSignin/components/StoreSignupInCarousel/StoreSignupInCarousel";

import { PromotionBanner, type PromotionBannerProps } from "./PromotionBanner/PromotionBanner";

import * as styles from "./StoreLandingLayout.styles";

interface Props extends Partial<PromotionBannerProps> {
  children: ReactNode | ((storefrontConfig: StoreConfigType | null) => ReactNode);
  storeName: string;
  storeSlug?: string;
  pracAvatar?: string;
  isPreview?: boolean;
  isStoreOwner?: boolean;
  storeId?: string;
  isStorefronts?: boolean;
  organizationLogo?: string;
  iconLogo?: string;
}

const StoreLandingLayout = ({
  children,
  storeName,
  storeSlug,
  pracAvatar,
  isPreview,
  isStoreOwner,
  isActivePromotion,
  storeId,
  isStorefronts,
  organizationLogo,
  iconLogo,
}: Props) => {
  const { phoneLarge } = useBreakpoints();

  const { data: storeConfigData, loading: storeConfigLoading } = useStoreConfigQuery({
    variables: { storeSlug },
    skip: !storeSlug,
  });

  const storefrontConfig = storeConfigData?.viewer?.storefrontConfig;

  const renderLeftColumnBlock = () => {
    if (isStorefronts) {
      return <StorefrontWelcomeLogo organizationLogo={organizationLogo} storeName={storeName} />;
    }

    return (
      <PatientInfoBlock
        storeName={storeName}
        pracAvatar={pracAvatar}
        customMarketing={storefrontConfig?.customAuthPageMarketingCopy}
        loading={storeConfigLoading}
      >
        <StoreSignupInCarousel />
        <PromotionBanner
          isActivePromotion={isActivePromotion}
          storeId={storeId}
          storeName={storeName}
        />
      </PatientInfoBlock>
    );
  };

  return (
    <Page>
      <StoreLandingPageNavBar
        isPreview={isPreview}
        isStoreOwner={isStoreOwner}
        isStorefronts={isStorefronts}
        iconLogo={iconLogo}
      />
      <div css={styles.layoutContainer}>
        <Columns css={styles.columnsContainer}>
          {!!phoneLarge.greaterThan && (
            <Column columnWidth={6} css={styles.leftColumn}>
              {renderLeftColumnBlock()}
            </Column>
          )}
          <Column columnWidth={6} css={styles.rightColumn}>
            {typeof children === "function" ? children(storefrontConfig ?? null) : children}
          </Column>
        </Columns>
      </div>
      <StoreLandingFooter />
    </Page>
  );
};

export { StoreLandingLayout };
